import React, { useEffect, useState } from "react";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Calendar as CalendarUI } from "@/components/ui/calendar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlarmClockOff, Ban, CheckCircle2, LogOut, PhoneCall, Save, UserRound } from "lucide-react";
import { toast } from "sonner";

export default function DoctorPortal() {
  const { t } = useLang();
  const [doctor, setDoctor] = useState(null);
  const [email, setEmail] = useState("rajesh@hospital.com");
  const [password, setPassword] = useState("doctor123");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("doctor_token");
    if (token) {
      api.doctorMe().then(setDoctor).catch(() => localStorage.removeItem("doctor_token"));
    }
  }, []);

  const login = async () => {
    setLoading(true);
    try {
      const { token, doctor } = await api.doctorLogin({ email, password });
      localStorage.setItem("doctor_token", token);
      setDoctor(doctor);
      toast.success("Welcome, " + doctor.name);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("doctor_token");
    setDoctor(null);
  };

  if (!doctor) {
    return (
      <div className="mx-auto max-w-md py-10">
        <Card className="border-slate-200 bg-white p-6">
          <h2 className="mb-6 text-2xl font-bold tracking-tight">{t("doctor_login")}</h2>
          <div className="grid gap-4">
            <div>
              <Label>{t("email")}</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="doctor-email-input" className="h-11" />
            </div>
            <div>
              <Label>{t("password")}</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="doctor-password-input" className="h-11" />
            </div>
            <Button onClick={login} disabled={loading} data-testid="doctor-login-btn" className="h-12 bg-teal-600 hover:bg-teal-700">
              {loading ? "..." : t("login")}
            </Button>
            <p className="text-xs text-slate-500">Try: rajesh@hospital.com / doctor123</p>
          </div>
        </Card>
      </div>
    );
  }

  return <DoctorDashboard doctor={doctor} setDoctor={setDoctor} onLogout={logout} />;
}

function DoctorDashboard({ doctor, setDoctor, onLogout }) {
  const { t } = useLang();
  const [appts, setAppts] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [cal, setCal] = useState(new Date());
  const [profile, setProfile] = useState({ name: doctor.name, department: doctor.department, fee: doctor.fee });

  const load = async () => {
    const [a, s] = await Promise.all([api.doctorAppts(), api.getSchedule()]);
    setAppts(a);
    setSchedule(s);
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.updateStatus(id, status);
      toast.success(`Updated to ${status}`);
      load();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const toggleDay = async (d, enabled) => {
    const dateStr = d.toISOString().split("T")[0];
    try {
      await api.toggleSlot(dateStr, enabled);
      toast.success(enabled ? "Date opened" : "Date closed");
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const emergencyCancel = async () => {
    const dateStr = cal.toISOString().split("T")[0];
    if (!confirm(`Emergency cancel ${dateStr}? 50% refund will be auto-processed.`)) return;
    try {
      const res = await api.emergencyCancel(dateStr);
      toast.success(`${res.affected.length} patient(s) refunded 50% & marked priority`);
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const saveProfile = async () => {
    try {
      await api.updateProfile({ ...profile, fee: Number(profile.fee) });
      setDoctor({ ...doctor, ...profile, fee: Number(profile.fee) });
      toast.success("Profile saved");
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const closedDates = new Set(schedule.filter((s) => !s.enabled).map((s) => s.date));
  const dateStr = cal.toISOString().split("T")[0];
  const isClosed = closedDates.has(dateStr);

  return (
    <div className="py-6">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-teal-100 text-teal-700">
            <UserRound className="h-7 w-7" />
          </div>
          <div>
            <h2 className="text-2xl font-bold tracking-tight">{doctor.name}</h2>
            <p className="text-sm text-slate-500">{doctor.department} · ₹{doctor.fee}</p>
          </div>
        </div>
        <Button variant="outline" onClick={onLogout} data-testid="doctor-logout-btn">
          <LogOut className="mr-1.5 h-4 w-4" /> {t("logout")}
        </Button>
      </div>

      <Tabs defaultValue="queue">
        <TabsList className="mb-6 bg-slate-100">
          <TabsTrigger value="queue" data-testid="tab-queue">{t("todays_queue")}</TabsTrigger>
          <TabsTrigger value="schedule" data-testid="tab-schedule">{t("schedule")}</TabsTrigger>
          <TabsTrigger value="profile" data-testid="tab-profile">{t("profile")}</TabsTrigger>
        </TabsList>

        <TabsContent value="queue">
          <div className="grid gap-3" data-testid="queue-list">
            {appts.length === 0 ? (
              <Card className="p-8 text-center text-slate-500">{t("no_appointments")}</Card>
            ) : appts.map((a) => (
              <Card key={a.id} className="border-slate-200 bg-white p-4" data-testid={`queue-${a.id}`}>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-slate-900 text-white">{a.token_id}</Badge>
                      <StatusBadge status={a.status} />
                    </div>
                    <div className="mt-2 font-semibold text-slate-900">{a.patient.name}, {a.patient.age}</div>
                    <div className="text-sm text-slate-500">{a.slot_time} · {a.patient.phone} · {a.disease || "—"}</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => setStatus(a.id, "calling")} data-testid={`call-${a.id}`}>
                      <PhoneCall className="mr-1 h-3.5 w-3.5" /> {t("call_patient")}
                    </Button>
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setStatus(a.id, "completed")} data-testid={`complete-${a.id}`}>
                      <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> {t("complete")}
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => setStatus(a.id, "rejected")} data-testid={`reject-${a.id}`}>
                      <Ban className="mr-1 h-3.5 w-3.5" /> {t("reject_wrong_dept")}
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="schedule">
          <div className="grid gap-6 md:grid-cols-2">
            <Card className="border-slate-200 bg-white p-4">
              <CalendarUI
                mode="single"
                selected={cal}
                onSelect={(d) => d && setCal(d)}
                modifiers={{ closed: schedule.filter((s) => !s.enabled).map((s) => new Date(s.date)) }}
                modifiersStyles={{ closed: { backgroundColor: "#fecaca", color: "#991b1b" } }}
                className="rounded-md"
              />
            </Card>
            <Card className="border-slate-200 bg-white p-5">
              <h3 className="mb-3 font-bold">{cal.toLocaleDateString()}</h3>
              <p className="mb-4 text-sm text-slate-500">
                Status: {isClosed ? <span className="font-semibold text-red-700">Closed</span> : <span className="font-semibold text-emerald-700">Open</span>}
              </p>
              <div className="grid gap-2">
                <Button onClick={() => toggleDay(cal, isClosed)} data-testid="toggle-day-btn" className="bg-slate-900 hover:bg-slate-800">
                  {isClosed ? t("open_date") : t("close_date")}
                </Button>
                <Button onClick={emergencyCancel} variant="destructive" data-testid="emergency-cancel-btn">
                  <AlarmClockOff className="mr-1.5 h-4 w-4" /> {t("emergency_cancel")}
                </Button>
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="profile">
          <Card className="max-w-xl border-slate-200 bg-white p-6">
            <div className="grid gap-4">
              <div>
                <Label>Name</Label>
                <Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} data-testid="profile-name-input" />
              </div>
              <div>
                <Label>Department</Label>
                <Input value={profile.department} onChange={(e) => setProfile({ ...profile, department: e.target.value })} data-testid="profile-dept-input" />
              </div>
              <div>
                <Label>OPD Fee (₹)</Label>
                <Input type="number" value={profile.fee} onChange={(e) => setProfile({ ...profile, fee: e.target.value })} data-testid="profile-fee-input" />
              </div>
              <Button onClick={saveProfile} data-testid="profile-save-btn" className="bg-teal-600 hover:bg-teal-700">
                <Save className="mr-1.5 h-4 w-4" /> {t("save")}
              </Button>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = {
    waiting: "bg-amber-100 text-amber-800",
    calling: "bg-sky-100 text-sky-800",
    completed: "bg-emerald-100 text-emerald-800",
    rejected: "bg-red-100 text-red-800",
    cancelled: "bg-slate-200 text-slate-700",
  };
  return <Badge className={colors[status] || "bg-slate-100"}>{status}</Badge>;
}
